import React, { memo } from 'react';
import { Link, useLocation } from 'wouter';
import { cn } from '../lib/utils';

export interface NavItem {
  href: string;
  label: string;
}

export const NAV_ITEMS: NavItem[] = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/programmes', label: 'Programmes' },
  { href: '/impact', label: 'Impact' },
  { href: '/partners', label: 'Partners' },
  { href: '/governance', label: 'Governance' },
  // { href: '/contact', label: 'Get involved' },
];

interface NavLinkProps {
  href: string;
  label: string;
  variant?: 'desktop' | 'mobile';
  isDark?: boolean;
  scrolled?: boolean;
  onClick?: () => void;
}

/**
 * Reusable navigation link component.
 * Handles active state styling for both desktop and mobile variants.
 */
const NavLink = memo(function NavLink({
  href,
  label,
  variant = 'desktop',
  isDark = false,
  scrolled = false,
  onClick,
}: NavLinkProps) {
  const [location] = useLocation();
  const isActive = location === href;

  if (variant === 'mobile') {
    return (
      <Link href={href}>
        <span
          className={cn(
            'block cursor-pointer rounded-2xl px-4 py-3 text-sm font-medium tracking-[0.01em] transition-all',
            isActive
              ? 'bg-primary/10 text-primary ring-1 ring-primary/15 shadow-[inset_0_0_0_1px_rgba(0,188,212,0.08)]'
              : isDark
                ? 'text-gray-200 hover:bg-gray-800/80'
                : 'text-gray-800 hover:bg-gray-100'
          )}
          onClick={onClick}
        >
          {label}
        </span>
      </Link>
    );
  }

  // Desktop variant
  return (
    <li>
      <Link
        href={href}
        className={cn(
          'rounded-full px-3.5 py-2 text-sm font-medium tracking-[0.01em] transition-all duration-300',
          isDark
            ? 'text-slate-100 hover:bg-white/10 hover:text-white'
            : scrolled
              ? 'text-slate-700 hover:bg-slate-100 hover:text-slate-950'
              : 'text-slate-900 hover:bg-slate-100 hover:text-slate-950',
          isActive && 'bg-primary/10 text-primary ring-1 ring-primary/15 shadow-[inset_0_0_0_1px_rgba(0,188,212,0.08)]'
        )}
      >
        {label}
      </Link>
    </li>
  );
});

NavLink.displayName = 'NavLink';

export default NavLink;
